import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'One identity, every app',
    description:
      'Federate access across SaaS, on-prem, and custom apps. Users sign in once, everything else follows.',
    bullets: [
      '7,000+ pre-built application integrations',
      'SAML 2, OpenID Connect, OAuth 2, JWT',
      'Custom REST API integration for in-house apps',
      'Browser extension for apps that lack a federation API',
    ],
  },
  {
    title: 'Built on standards, not lock-in',
    description:
      'Open protocols mean you can adopt Monosign today and still own your identity tomorrow. No proprietary token formats.',
    bullets: [
      'SAML 2.0 with ADFS-compatible assertions',
      'OIDC for modern apps and mobile clients',
      'OAuth 2 authorization flows for APIs',
      'Kerberos and LDAP fallback for legacy systems',
    ],
  },
  {
    title: 'Adaptive risk and context',
    description:
      'Treat sign-in as a continuous signal, not a one-time event. Score device, network, and behavior on every authentication.',
    bullets: [
      'Step-up MFA on anomalous sign-ins',
      'Device posture and trust evaluation',
      'IP, geolocation, and impossible-travel rules',
      'Session continuity across federated apps',
    ],
  },
  {
    title: 'Self-hosted or cloud: same API surface',
    description:
      'Deploy Monosign inside your VPC, on bare metal, or run it as a managed cloud tenant. Identical SAML and OIDC endpoints either way.',
    bullets: [
      'Helm and Docker deployment manifests',
      'High-availability and active-active topology',
      'Data residency in EU, Turkey, or your own region',
      'Disaster-recovery runbooks bundled in',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Federate your apps',
    description:
      'Connect SaaS apps from the catalog or onboard custom apps using SAML, OIDC, or our REST integration.',
  },
  {
    title: 'Define your policies',
    description:
      'Set MFA, conditional access, and session policies once; Monosign applies them consistently across every app.',
  },
  {
    title: 'Let users sign in once',
    description:
      'Users authenticate to Monosign and get seamless access to every connected app, with every event logged.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Multi-Factor Authentication',
    description:
      'Phishing-resistant factors, adaptive policies, and a clean enrollment flow.',
    href: '/sign/mfa',
  },
  {
    label: 'Monofor Identity',
    description:
      'Branded authenticator app for push, OTP, and passwordless prompts.',
    href: '/sign/authenticator',
  },
  {
    label: 'Directory & sources',
    description:
      'Connect AD, LDAP, OUD, and web APIs as identity sources.',
    href: '/sign/directory',
  },
]

export default function SsoPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Single Sign-On (SSO)"
        description="Federate access across every app (SaaS, on-prem, and custom) with SAML 2, OIDC, and OAuth 2. One sign-in, every app."
        path="/sign/sso"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Single Sign-On"
          title={
            <>
              One sign-in.
              <br />
              Every app.
            </>
          }
          body="Federate every workforce, partner, and contractor app behind one identity. SAML 2, OIDC, and OAuth 2 on day one: no proprietary protocols, no lock-in, no rebuild."
          badges={[
            '7,000+ pre-built integrations',
            'Self-hosted or cloud',
            'SAML 2 / OIDC / OAuth 2',
          ]}
        />
        <SolutionPillars
          title={<>Everything sign-on, none of the lock-in.</>}
          body="Monosign Single Sign-On replaces a tangle of vendor logins with one phishing-resistant identity, governed by your policies and logged for your auditors."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to one sign-in.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
